import type { KeyboardEvent, MouseEvent, ReactNode, RefObject } from 'react';
import { useEffect, useId, useRef } from 'react';
import { Button } from '../../atoms/button/button.js';
import { Heading } from '../../atoms/heading/heading.js';

/**
 * Modal — the dialog shell: scrim, panel, title block, a slot for the body, and
 * the dismissal contract that goes with them.
 *
 * Corpus: `DB:38-44`. A fixed scrim at `rgba(20,20,25,0.44)` with
 * a 3px backdrop blur, padded `10vh 16px 16px` so the panel hangs from the top
 * rather than centring; the panel 100% wide to a 460px cap, white at radius 20,
 * padding 22px, entering with `prUp` over 0.24s; a 19px bold title over a 13.5px
 * secondary line 3px below it. `:2871` is the backdrop's `stop` handler, shared
 * by every overlay in that file — the picker, the report viewer, the record
 * sheet and the emergency dialog are one shell with four bodies, which is the
 * argument for one Modal rather than four.
 *
 * **Everything the corpus omits is required here.** The design file has no focus
 * move, no focus trap, no focus return and no Escape: it is a picture of a
 * dialog, not a dialog. `modal.test.tsx` pins all four, because a behaviour
 * demonstrated only in a story is a behaviour the next refactor removes.
 *
 * **The name comes from the rendered heading**, through `aria-labelledby`, and
 * not from the `aria-label` at `:39`: an accessible name held in an attribute
 * beside the visible title is a name that can drift from it. `aria-describedby`
 * points at the subtitle when there is one.
 *
 * **Scrim click is a pointer affordance only**, so it is paired with Escape and
 * with a real close button or the dialog is unreachable from a keyboard. It is
 * implemented by comparing the event's target with the scrim itself rather than
 * by the corpus's `stopPropagation` at `:40`: stopping propagation at the panel
 * silences every listener above it, including ones a caller legitimately put
 * there, and this needs to know one thing only — whether the press landed on the
 * scrim or inside the dialog.
 *
 * **What this does NOT do, stated rather than implied: it does not make the rest
 * of the page inert.** `aria-modal="true"` declares containment to assistive
 * technology and the Tab cycle below holds the keyboard, but neither stops a
 * pointer or a virtual cursor reaching the page behind. Doing that properly
 * means putting `inert` on the application root, and this component has no
 * handle on it — taking one as a prop would make every caller responsible for
 * getting it right, which is how it ends up wrong. It belongs to the app shell
 * template, which owns the root and can do it once. That decision carries
 * the related caveat this shape brings with it: a `position: fixed`
 * scrim is laid out against the nearest ancestor with a transform, filter or
 * `will-change`, so a dialog rendered inside a transformed card covers that card
 * rather than the viewport. Both are one fix — an overlay root — and both belong
 * to whoever owns the root.
 *
 * **The focus trap models element types, not native tab order.** `isTabbable`
 * rejects the untabbable — hidden, inert, disabled-by-fieldset, `tabindex="-1"`
 * — but it does not model a radio group, where only the checked radio is a Tab
 * stop. A dialog whose last control is an unchecked radio therefore has a cycle
 * end the browser will not stop at. Stated rather than implied: closing it means
 * modelling sequential focus navigation properly, which is a focus-trap library
 * rather than a component, and the same overlay work is where that decision
 * belongs.
 *
 * **Not portalled**, for the same reason the corpus is not: `:40` sets `dir` on
 * the panel precisely because a portalled dialog inherits nothing. Rendering in
 * place means direction, language and every custom property come from the tree
 * the caller put it in, and there is no second copy of that state to get wrong.
 *
 * Values with no token: the panel's 22px padding (`--rp-pad-card` is 24px; 22px
 * is `--rp-gutter-app`, which means something else), the 460px cap, the
 * `10vh 16px 16px` scrim padding, the 3px title gap, `z-index: 200` — no layer
 * scale exists, and the skip link claims 100/200 as well — and the `prUp`
 * keyframe itself, whose duration is tokenised while the frames live in each
 * design file's `<style>`. `modal.css` names each at the rule that draws it.
 *
 * It owns the shell and the dismissal contract and owns no content, so nothing
 * inside it is a literal string.
 */
export interface ModalProps {
  /** `false` renders nothing at all — the corpus gates the whole subtree (`:38`). */
  readonly open: boolean;
  /**
   * Copy, and the dialog's accessible name. A blank one keeps the dialog shut:
   * this component takes focus and holds it, so an unnamed dialog is a keyboard
   * trap with nothing to identify it, and the component may not invent a name.
   */
  readonly title: string;
  /** Copy. */
  readonly subtitle?: string | undefined;
  /** One handler for the scrim, Escape and the close control. */
  readonly onClose: () => void;
  /** Copy. The close control's accessible name; blank keeps the dialog shut too. */
  readonly closeLabel: string;
  readonly children: ReactNode;
  /** An action row below the body. */
  readonly footer?: ReactNode | undefined;
  /** Defaults to the corpus 460px. Any CSS length. */
  readonly maxWidth?: string | undefined;
  /** Default `true`. `false` for a dialog that a stray click should not lose. */
  readonly dismissOnScrimClick?: boolean | undefined;
  /** Where focus lands on open. Defaults to the close control. */
  readonly initialFocusRef?: RefObject<HTMLElement | null> | undefined;
}

/**
 * Element types that can hold focus. A first pass, never the answer — see below.
 *
 * `summary` and the media controls are here because a dialog body is the
 * caller's markup and a `<details>` inside one is ordinary: without its summary
 * in this set the cycle collapsed onto the close button, which then prevented
 * every Tab and refocused itself, leaving the disclosure unreachable by keyboard.
 * Raised on the seventh review pass.
 */
const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Whether an element the selector matched is somewhere Tab will actually go.
 *
 * The selector alone is not enough and the gap is not theoretical: a `[hidden]`
 * or `inert` control after the last visible one becomes the cycle's `last`, so
 * Tab from the visible control is not intercepted, the browser skips the hidden
 * one, and focus leaves the dialog for the page behind it — which the trap
 * exists to prevent and `aria-modal` only declares away.
 *
 * `hidden`, `inert` and a disabled `<fieldset>` are checked through ancestors,
 * because all three apply to a subtree. `tabIndex < 0` catches the control the
 * selector cannot express — an `<a href>` carrying `tabindex="-1"` matches
 * `a[href]` whatever its tabindex says. Display and visibility are read from
 * computed style, which is authoritative in a browser; under jsdom it resolves
 * inline styles only, so this is the half that runs everywhere and the browser
 * is where the other half is real.
 */
function isTabbable(element: HTMLElement): boolean {
  if (element.tabIndex < 0) return false;
  if (element.closest('[hidden], [inert], [aria-hidden="true"], fieldset[disabled]') !== null) {
    return false;
  }
  const style = element.ownerDocument.defaultView?.getComputedStyle(element);
  return style?.display !== 'none' && style?.visibility !== 'hidden';
}

export function Modal({
  open,
  title,
  subtitle,
  onClose,
  closeLabel,
  children,
  footer,
  maxWidth = '460px',
  dismissOnScrimClick = true,
  initialFocusRef,
}: ModalProps) {
  const titleId = useId();
  const subtitleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  // An untranslated catalogue key would otherwise open a dialog with an empty
  // heading for its accessible name and an empty `aria-label` on the only way
  // out of it — and this component moves focus into that and holds it there,
  // which turns a missing string into a keyboard trap with no name. It may not
  // invent either word, so it declines to open; `Chip` and `NavItem` decline to
  // render for the same reason.
  //
  // Computed here rather than at the render guard, and it is what the focus
  // effect watches: with `open` alone as the dependency, a dialog opened while
  // its copy was still loading rendered nothing on the first pass and then
  // appeared without the effect re-running, leaving focus on the page behind a
  // dialog that had already declared itself modal. Raised on the fifth review
  // pass; the guard and the effect now turn on the same value.
  const canOpen = open && title.trim() !== '' && closeLabel.trim() !== '';

  useEffect(() => {
    if (!canOpen) return;
    // Where focus was before the dialog took it. Read at open rather than at
    // close: by the time the dialog closes, the element that opened it is no
    // longer active and there is nothing left to read.
    const opener = document.activeElement;
    // The close control is found in the panel rather than held on a ref, so
    // `Button` does not have to forward one for this component's sake.
    const fallback = panelRef.current?.querySelector<HTMLElement>('.rp-modal__close');
    // The caller's element only if it is actually inside the dialog. A ref
    // pointing at something on the page behind would put focus there and leave
    // it there: Escape and Tab dispatched from outside never reach the scrim, so
    // neither the close handler nor the trap would run, and the dialog would be
    // declaring `aria-modal` over a page the reader is still standing in.
    // Raised on the seventh review pass.
    const requested = initialFocusRef?.current ?? null;
    const inside = requested !== null && panelRef.current?.contains(requested) === true;
    (inside ? requested : fallback)?.focus();
    return () => {
      // A dialog that closes and leaves focus on `<body>` drops a keyboard user
      // back at the top of the page, with no way to know where they were.
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
    // `initialFocusRef` is a ref object, so its identity is stable and it is not
    // a dependency; re-running this on every render would re-steal focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canOpen]);

  if (!canOpen) return null;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    // The trap. Without it Tab walks out of the dialog and into the page behind,
    // which is still there and still clickable — see the note in the header.
    const focusable = [
      ...(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []),
    ].filter(isTabbable);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !panelRef.current?.contains(active))) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  const onScrimClick = (event: MouseEvent<HTMLDivElement>) => {
    // Only a press that landed on the scrim itself. The corpus stops propagation
    // at the panel instead (`:40`), which silences every listener above it.
    if (dismissOnScrimClick && event.target === event.currentTarget) onClose();
  };

  return (
    /* The scrim is not a control and takes no role: it carries the press that
       lands outside the panel, and the keyboard equivalent is Escape, which is
       handled on the same element because the dialog inside it has focus. */
    <div className="rp-modal__scrim" onClick={onScrimClick} onKeyDown={onKeyDown}>
      <div
        aria-describedby={subtitle === undefined ? undefined : subtitleId}
        aria-labelledby={titleId}
        aria-modal="true"
        className="rp-modal__panel"
        ref={panelRef}
        role="dialog"
        style={{ maxInlineSize: maxWidth }}
      >
        <div className="rp-modal__head">
          <div className="rp-modal__titles">
            <Heading id={titleId} level={2} size="panel">
              {title}
            </Heading>
            {subtitle === undefined ? null : (
              <p className="rp-modal__subtitle" id={subtitleId}>
                {subtitle}
              </p>
            )}
          </div>
          <Button
            aria-label={closeLabel}
            className="rp-modal__close"
            onClick={onClose}
            variant="secondary"
          >
            <span aria-hidden="true">✕</span>
          </Button>
        </div>
        <div className="rp-modal__body">{children}</div>
        {footer === undefined ? null : <div className="rp-modal__footer">{footer}</div>}
      </div>
    </div>
  );
}
